/* eslint-disable @typescript-eslint/no-empty-function */
import MapboxDraw from '@mapbox/mapbox-gl-draw';
import { toFeature } from '@utils/validation/coordinates.utils';
import { Map } from 'mapbox-gl';
import { RefObject, useEffect } from 'react';
import { type ControlPosition, IControl, useControl } from 'react-map-gl/mapbox';

import { ZoneToEditType } from '@/types/config/config.types';
import { MapBoxDrawEvent } from '@/types/map.types';

type DrawPolygonControlProps = ConstructorParameters<typeof MapboxDraw>[0] & {
  drawRef?: RefObject<MapboxDrawControl | null>;
  position?: ControlPosition;
  zoneToEdit?: ZoneToEditType;

  onCreate?: (evt: { features: MapBoxDrawEvent[] }) => void;
  onUpdate?: (evt: { features: MapBoxDrawEvent[]; action: string }) => void;
};

export type MapboxDrawControl = MapboxDraw & IControl;

export function DrawPolygonControl({
  drawRef,
  position,
  zoneToEdit,
  onCreate = () => {},
  onUpdate = () => {},
  ...props
}: DrawPolygonControlProps) {
  const onDrawUpdate = (map: Map, evt: { features: MapBoxDrawEvent[]; action: string }) => {
    onUpdate(evt);
    map.dragPan.enable();
  };

  const drawInstance = useControl<MapboxDrawControl>(
    () => new MapboxDraw(props) as MapboxDrawControl,
    ({ map }) => {
      const mbMap = map.getMap() as Map;

      map.on('draw.create', onCreate);
      map.on('draw.update', (evt) => onDrawUpdate(mbMap, evt));

      if (drawRef) {
        drawRef.current = drawInstance;
      }
    },
    ({ map }) => {
      const mbMap = map.getMap() as Map;

      map.off('draw.create', onCreate);
      map.off('draw.update', (evt) => onDrawUpdate(mbMap, evt));
    },
    {
      position: position,
    },
  );

  // trigger after useControl, since useControl allocates drawInstance to map.
  useEffect(() => {
    if (
      !zoneToEdit?.polygon ||
      ('type' in zoneToEdit.polygon ? zoneToEdit.polygon.coordinates[0] : zoneToEdit.polygon).length === 0
    )
      return;

    drawInstance.deleteAll();
    if (zoneToEdit.id) {
      drawInstance.add(toFeature(zoneToEdit.polygon, JSON.stringify(zoneToEdit.id)));

      drawInstance.changeMode('direct_select', {
        featureId: JSON.stringify(zoneToEdit.id),
      });
    } else {
      const tempFeatureId = 'creation-id';
      drawInstance.add(toFeature(zoneToEdit.polygon, tempFeatureId));
      drawInstance.changeMode('direct_select', {
        featureId: tempFeatureId,
      });
    }
  }, [zoneToEdit, drawInstance]);

  return null;
}
